/**
 * ApprovalSlaStrip — Tier-2 approval-SLA card on the tablet idle
 * dashboard (Plan 02-17 Task 1 / UI-SPEC §13 / D-06 / CHORE-09).
 *
 * #160 (Stell_Kjokken): glasskort med klokke nederst i tredje kolonne, ikke
 * lenger et gult bånd øverst. Skissens to linjer («3 venter» + «1 har ventet
 * over 24 timer») trenger totalen, som vi ikke har her; tallet er fortsatt
 * bare Tier-2.
 *
 * Renders in the idle dashboard whenever the live Tier-2
 * count (rows with `awaiting_approval` and `second_nudge_sent_at IS NOT
 * NULL`) is ≥ 1. count=0 hides the strip entirely (no fade — the strip
 * either is or isn't, per UI-SPEC §13).
 *
 * Tap behaviour: navigates to /godkjenninger. The route is admin-only
 * (Plan 02-10 — non-admins see the "Bare voksne kan godkjenne" 403
 * fallback), so the strip is just a deep link. T-02-41 mitigation.
 *
 * Copy uses i18next plural form (chores.openCount pattern):
 *   approvals.sla.tabletStrip_one  → "1 oppgave venter på godkjenning"
 *   approvals.sla.tabletStrip_other → "{{count}} oppgaver venter på godkjenning"
 *
 * Note: the plan's <action> reference text shows ICU-style
 * `{count, plural, one{...} other{...}}` syntax — the rest of the app
 * uses i18next-native `_one`/`_other` pluralisation (chores.openCount,
 * streaks.freeze.pill, photos.retentionFooter, etc.). Same outcome,
 * single i18n convention preserved (deviation Rule 1: stylistic).
 */
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { Icon } from '../../components/primitives/Icon';

interface ApprovalSlaStripProps {
  count: number;
}

export function ApprovalSlaStrip({ count }: ApprovalSlaStripProps): ReactNode {
  const nav = useNavigate();
  const { t } = useTranslation();
  if (count <= 0) return null;
  // Varselet ligger rundt knappen, ikke på den: role="alert" på en <button>
  // tar over knapperollen, og knappen mister navnet sitt (axe button-name, #160).
  return (
    <div role="alert" aria-live="assertive">
      <button
        type="button"
        data-testid="approval-sla-strip"
        onClick={() => nav('/godkjenninger')}
        className={`${GLASS_SURFACE} text-ink flex min-h-11 w-full items-center gap-3.5 rounded-lg px-4.5 py-4 text-left text-lg font-extrabold`}
      >
        <Icon.Clock size={28} className="text-warn shrink-0" />
        {t('approvals.sla.tabletStrip', { count })}
      </button>
    </div>
  );
}
